if (!customElements.get('marketing-form')) {
  customElements.define(
    'marketing-form',
    class MarketingForm extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLFormElement | null} */
        this.form = this.querySelector('form');
      }

      connectedCallback() {
        if (this.form) {
          this.form.addEventListener('submit', this.submitHandler.bind(this));
        }
      }

      /**
       * @param {Event} e - The form submit event
       * @returns {Promise<any>} - The response from the server
       */
      async submitHandler(e) {
        e.preventDefault();

        if (!this.form) return;

        this.form.dataset.loading = 'true';

        try {
          /** @type {any} */
          const formElements = this.form.elements;
          const formData = {
            email: formElements['email'].value.trim(),
            interest: formElements['interest'].value.trim(),
          };

          if (!formData.email) {
            this.showErrors(['Please fill in all required fields']);
            return;
          }

          await this.sendToKlaviyo(
            {
              ...formData,
            },
            this.form.id,
          );

          this.dataset.success = 'true';
        } catch (/** @type {any} */ error) {
          if (error.name === 'AbortError') return;

          window.Sentry?.captureException(error, {
            tags: {
              feature: 'marketing-form',
              error_type: error.name || 'Error',
            },
            extra: {
              formId: this.form.id,
              status: error.status,
            },
          });

          this.showErrors(['Something went wrong. Please try again later.']);
        } finally {
          this.form.dataset.loading = 'false';
        }
      }

      /**
       * @param {import('./types').Customer} customer - The customer to send to Klaviyo
       * @param {string} id - The form ID
       * @returns {Promise<void>} - A promise that resolves when the request is complete
       */
      async sendToKlaviyo(customer, id) {
        /** @type {Record<string, unknown>} */
        const profileAttributes = {
          email: customer.email,
          subscriptions: {
            email: {
              marketing: {
                consent: 'SUBSCRIBED',
              },
            },
          },
        };

        if (customer.firstName) profileAttributes.first_name = customer.firstName;
        if (customer.lastName) profileAttributes.last_name = customer.lastName;
        if (customer.id) profileAttributes.external_id = customer.id;

        /** @type {Record<string, string>} */
        const properties = {};

        if (customer.birthday) properties.Birthday = customer.birthday;

        if (Object.keys(properties).length > 0) profileAttributes.properties = properties;

        const payload = {
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttributes,
                },
              },
              custom_source: id,
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: 'YwGeYA',
                },
              },
            },
          },
        };

        const response = await fetch('https://a.klaviyo.com/client/subscriptions?company_id=WCbtTN', {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            Revision: '2025-01-15',
            'Content-Type': 'application/vnd.api+json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorData = await response.json();
          /** @type {any} */
          const error = new Error(errorData.errors?.[0]?.detail || 'Unknown error');
          error.status = response.status;
          throw error;
        }
      }

      /** @param {string[]} errors - The errors to display */
      showErrors(errors) {
        const errorContainer = this.querySelector('.form-errors');

        if (!errorContainer) return;

        errorContainer.innerHTML = errors.map((/** @type {string} */ error) => `<li>${error}</li>`).join('');
      }
    },
  );
}
